import { RootFiIntegration } from '@commenda/prisma';
import { SupportedDateFormatforCsvMapping } from '../../sales-tax/types';
import { Address } from '../calculations/types';
import { SalesTaxCustomer } from '../customer/types';
import { ExemptionReasonEnum } from '../exemptions/types';
import { BulkUploadProps, BulkUploadResponse } from '../products/types';

export const MANUAL_UPLOAD_SOURCE_PLATFORM = 'MANUAL_UPLOAD';

export enum TransactionStatus {
  LOCKED = 'LOCKED',
  UNCOMMITTED = 'UNCOMMITTED',
  COMMITTED = 'COMMITTED',
  VOIDED = 'VOIDED',
}

export enum TransactionType {
  SALE = 'SALE',
  REFUND = 'REFUND',
  PURCHASE = 'PURCHASE',
  PURCHASE_REFUND = 'PURCHASE_REFUND',
}

/**
 * Metadata type for DE/GB transactions.
 * Used to store additional VAT-related information that doesn't have
 * a direct 1:1 mapping to the transaction model.
 */
export interface TransactionMetadataType {
  /** Original transaction type from CSV (preserves PURCHASE even when invoice_type is REFUND) */
  transactionType?: 'SALE' | 'REFUND' | 'PURCHASE' | 'PURCHASE_REFUND';
  /** VAT registration number */
  vatNumber?: string;
  /** Reverse charge for this transaction */
  reverseCharge?: string;
}

export enum TransactionCreationMethod {
  TRANSACTIONS_API = 'TRANSACTIONS_API',
  TRANSACTIONS_API_BULK = 'TRANSACTIONS_API_BULK',
  UNKNOWN = 'UNKNOWN',
}

export enum SalesTaxTransactionExtend {
  SHIPPING_ADDRESS = 'shipping_address',
  CUSTOMER = 'customer',
}

export enum Marketplace {
  AMAZON = 'AMAZON',
}

export enum TaxRemitterType {
  MARKETPLACE_FACILITATOR = 'MARKETPLACE_FACILITATOR',
  SELLER = 'SELLER',
  BUYER = 'BUYER',
}

export enum CalculationTaxBreakdownJurisdictionType {
  STATE = 'STATE',
  COUNTY = 'COUNTY',
  CITY = 'CITY',
  DISTRICT = 'DISTRICT',
  LOCAL = 'LOCAL',
  TRADEBLOC = 'TRADEBLOC',
  COUNTRY = 'COUNTRY',
  STATE_OR_PROVINCE = 'STATE_OR_PROVINCE',
  RESERVATION = 'RESERVATION',
}

export enum CalculationTaxBreakdownJurisdictionNameMap {
  STATE = 'State',
  COUNTY = 'County',
  CITY = 'City',
  DISTRICT = 'District',
  LOCAL = 'Local',
  TRADEBLOC = 'Trade Bloc',
  COUNTRY = 'Country',
}

export interface JurisdictionBreakdownItem {
  jurisdiction_type: CalculationTaxBreakdownJurisdictionType;
  jurisdiction_name: string;
  rate: number;
  tax_amount: number;
}

export interface TaxBreakdownSummary {
  jurisdiction_breakdown: JurisdictionBreakdownItem[];
  estimated_taxable_amount: number;
  estimated_non_taxable_amount: number;
  estimated_tax_amount: number;
  effective_tax_rate: number;
}

export interface TransactionLineItemProduct {
  product_id: string;
  product_sku: string;
}

export interface TransactionLineItem {
  id: string;
  amount: number;
  quantity: number;
  shipping: number;
  handling: number;
  discount: number;
  price_per_unit: number;
  tax_collected: number;
  tax_code?: string;
  product_id?: string;
  quantity_refunded?: number;
  exemption_type?: ExemptionReasonEnum;
  exemption_certificate_id?: string;
  tax_included?: boolean;
  product?: TransactionLineItemProduct;
}

export interface TransactionCustomerDetails {
  shipping_address: Address;
  customer_id: string;
}

export interface Transaction {
  // Base fields
  id: string;

  // Core transaction fields
  corporation_id: string;
  transacted_at: string;
  created_at: number;
  reporting_date_override?: string;
  calculation_date_override?: string;

  // Financial details
  invoice_currency: string;
  subtotal: number;
  discount: number;
  shipping_and_handling: number;
  total: number;
  tax_collected: number;

  // Status and type
  invoice_status: TransactionStatus;
  invoice_type: TransactionType;
  refund_type?: string;

  // Source and identifiers
  source_platform?: RootFiIntegration;
  source_platform_id?: string;
  invoice_number?: string;

  // Creation and relationships
  creation_method?: TransactionCreationMethod;
  parent_transaction_id?: string;

  // Addresses
  shipping_address?: Address;

  // Customer
  customer?: SalesTaxCustomer;

  // Additional fields
  exemption_type?: ExemptionReasonEnum;
  customer_id?: string;
  filing_id?: string;
  marketplace?: Marketplace;
  tax_remitter_type?: TaxRemitterType;
  customer_business_identification_number?: string | null;
}

export interface TransactionExtended extends Transaction {
  customer_details: TransactionCustomerDetails;
  ship_from_address?: Address;
  line_items?: TransactionLineItem[];
}

export interface TransactionListItem {
  id: string;
  type: TransactionType;
  status: TransactionStatus;
  date: string;
  reportingDate: string;
  subTotal: number;
  taxCollected: number;
  discount: number;
  shipping_and_handling: number;
  creationMethod: TransactionCreationMethod;
  total: number;
  sourcePlatform: RootFiIntegration;
  sourcePlatformId: string;
  invoiceNumber: string;
  destinationCountry: string;
  destinationState: string;
  invoiceCurrency?: string;
  taxBreakdownSummary?: TaxBreakdownSummary;
  customer?: SalesTaxCustomer;
  metadata?: TransactionMetadataType;
  exemptionType?: ExemptionReasonEnum;
  customerBusinessIdentificationNumber?: string | null;
  taxEmitterType?: TaxRemitterType;
}

export interface BulkUploadTransactionsParams extends BulkUploadProps {
  dateFormat: SupportedDateFormatforCsvMapping;
  isTaxInclusiveTransactions: boolean;
  defaultQuantity: number;
  /**
   * BUG-1074: when true, the logical backend copies each row's transaction date into
   * reporting_date_override before forwarding to the tax backend. Manual upload only.
   */
  copyTransactionDateToReportingDate?: boolean;
}

/**
 * Logical-backend serializes the sales-tax-api-2 response from snake_case to
 * camelCase, so this interface tracks the camelCase wire format the frontend
 * actually receives. Do NOT switch this back to snake_case — that would
 * diverge from the NestJS `TransactionAggregate` type and silently produce
 * NaN in every monetary card.
 */
export interface TransactionAggregates {
  totalTransactions: number;
  totalSales: number;
  totalTaxCollected: number;
  /** Net purchases: PURCHASE - PURCHASE_REFUND, in the response currency. */
  totalPurchases: number;
  /** Net input tax (purchase-side), in the response currency. */
  totalTaxPaid: number;
  /** Currency the totals are reported in (defaults to USD). */
  currency?: string;
  /** Echo of the caller's target_currency request, when supplied. */
  targetCurrency?: string;
  /** Invoices skipped from totals because no FX rate was available. */
  excludedInvoiceCount?: number;
  /** Invoices whose conversion fell back to a prior date (no exact-date rate). */
  priorRateUsedCount?: number;
  /** Per-period breakdown when group_by is requested. */
  dateGroups?: TransactionAggregateDateGroups;
}

export interface TransactionAggregateDateGroups {
  groupBy: string;
  groups: TransactionAggregateDateGroup[];
}

export interface TransactionAggregateDateGroup {
  date: string;
  totalTransactions: number;
  totalSales: number;
  totalTaxCollected: number;
  totalPurchases: number;
  totalTaxPaid: number;
  currency?: string;
}

export interface TransactionListResponse {
  data: TransactionListItem[];
  nextCursor: string | null;
  totalTransactions: number;
  aggregates?: TransactionAggregates;
}

export interface ListTransactionsParams {
  token?: string;
  filters: {
    corporationId?: number;
    to_date?: string;
    from_date?: string;
    reporting_date_override_from_date?: string;
    reporting_date_override_to_date?: string;
    creation_methods?: string;
    source_platforms?: string;
    destination_countries?: string[];
    destination_states?: string[];
    currency?: string[];
    target_currency?: string;
    statuses?: TransactionStatus[];
    search_term?: string;
    type?: TransactionType;
    marketplaces?: Marketplace[];
  };
  cursor?: string | null;
  limit?: number;
  showTaxBreakdown?: boolean;
  sortState: SortState;
}

export interface Destination {
  country: string;
  states?: string[];
}

export interface TransactionBulkActionFilters {
  from_date?: string;
  to_date?: string;
  reporting_date_override_from_date?: string;
  reporting_date_override_to_date?: string;
  ship_to_addresses?: Destination[];
  currencies?: string[];
  transaction_creation_methods?: TransactionCreationMethod[];
  source_platforms?: (
    | RootFiIntegration
    | typeof MANUAL_UPLOAD_SOURCE_PLATFORM
  )[];
  statuses?: TransactionStatus[];
  search_term?: string;
  type?: TransactionType;
  marketplaces?: Marketplace[];
}

export interface BulkActionTransactionsParams {
  corporation_id: number;
  transaction_ids?: string[];
  filters?: TransactionBulkActionFilters;
  excluded_ids?: string[];
}

export interface BulkDeleteTransactionsByIdsResponse {
  data: {
    job: string;
    total_ids: number;
  };
  message: string;
}

export interface BulkDeleteTransactionsByFiltersResponse {
  message: string;
}

// EXPORT DATA TYPES

export enum ExportDataDeliveryType {
  EMAIL = 'email',
}

export interface ExportDataDeliveryOptions {
  type: ExportDataDeliveryType;
  recipients?: string[];
}

export interface BulkExportTransactionsRequest
  extends BulkActionTransactionsParams {
  delivery: ExportDataDeliveryOptions;
}

export interface BulkExportTransactionsResponse {
  message: string;
}

export interface TransactionFilterAllowedOptionsResponse {
  data: {
    source_platforms: (
      | RootFiIntegration
      | typeof MANUAL_UPLOAD_SOURCE_PLATFORM
    )[];
    jurisdictions: string[];
    countries: string[];
    marketplaces: Marketplace[];
  };
  message: string;
}

export interface TransactionExtendedResponse {
  data: TransactionExtended;
  message: string;
}

export enum BulkExportReportType { }

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export type SortState = {
  id: string;
  desc: boolean;
};
